/* Constants */
import {HTTP_STATUS_CODES} from '../../common/network/httpConstants';
import {
  FILTER_QUERY_PARAM_NAMES,
  FACET_IDS,
  FACET_QUERY_PARAM_NAMES,
} from '../../views/filter/facet/facetConstants';
import {PAGINATION_PARAM_NAMES} from '../../common/pagination/paginationConstants';
import {
  CATEGORY_PAGE_LINK_WITH_BRAND_REGEX
} from './categoryConstants';
import {
  PLATFORMS,
  ROUTE_PATHS,
} from '../../core/route/routeConstants';
import {TOGGLED_CATEGORY_PAGINATION_PAGE_NUMBER_LIMIT} from './categoryPaginationConstants';

/* Utils */
import {matchUrlForRoutePath} from '../../core/route/routeUtils';
import {generateQueryString} from '../../common/url/urlUtils';
import {generateFilterApiQueryParams} from '../filter/filterUtils';
import {canRedirectToFirstPage} from '../../common/pagination/paginationUtils';
import {isApiPaginationLimitExceeded} from '../../common/network/apiUtils';
import {createObjectFromStringArray} from '../../common/collection/collectionUtils';
import {generateStructuredBreadCrumbDataProps} from '../seo/seoUtils';

const CATEGORY_URL_DELIMITER = '-c-';

function checkForCategoryRedirection(
  categoryResponseData,
  currentCategoryUrlKeyword,
  currentCategoryId,
  currentQueryParams,
  currentBrandIds,
  landingPageData,
  isCollectionPage
) {
  let redirection;

  if (!categoryResponseData) {
    return null;
  }

  if (categoryResponseData?.redirection && categoryResponseData?.redirection?.url) {
    redirection = {
      ...categoryResponseData.redirection,
    };
  } else if (!isCollectionPage && categoryResponseData?.data?.categoryUrlKeyword !== currentCategoryUrlKeyword) {
    redirection = {
      isInternal: true,
      type: HTTP_STATUS_CODES.MOVED_PERMANENTLY,
      url: generateCategoryUrl(
        categoryResponseData.data.categoryUrlKeyword || currentCategoryUrlKeyword,
        currentCategoryId,
        {
          brandIds: currentBrandIds,
          queryParams: currentQueryParams,
        },
      ),
    };
  } else if (
    landingPageData &&
    (currentBrandIds || currentQueryParams)
  ) {
    const willBeDeletedPaginationParams = Object.values(PAGINATION_PARAM_NAMES);
    const willBeDeletedFilterParams = Object.values(FILTER_QUERY_PARAM_NAMES);

    const willBeDeletedQueryParams = {
      ...createObjectFromStringArray(
        [].concat(willBeDeletedPaginationParams, willBeDeletedFilterParams),
        null,
      ),
    };

    const categoryUrl = generateCategoryUrl(
      currentCategoryUrlKeyword,
      currentCategoryId,
      {
        brandIds: null,
        queryParams: {
          ...currentQueryParams,
          ...willBeDeletedQueryParams,
        },
      },
    );

    redirection = {
      isInternal: true,
      type: HTTP_STATUS_CODES.MOVED_PERMANENTLY,
      url: categoryUrl,
    };
  } else if (categoryResponseData?.data?.products?.length === 0 && !canRedirectToFirstPage(currentQueryParams)) {
    redirection = {
      isInternal: true,
      type: HTTP_STATUS_CODES.MOVED_PERMANENTLY,
      url: generateCategoryUrl(
        categoryResponseData.data.categoryUrlKeyword,
        currentCategoryId,
        {
          queryParams: {
            ...currentQueryParams,
            [PAGINATION_PARAM_NAMES.PAGE]: null,
          },
          brandIds: currentBrandIds,
        }
      ),
    };
  }

  return redirection;
}

function generateCategoryUrl(
  currentCategoryUrlKeyword,
  currentCategoryId,
  {
    queryParams,
    brandIds,
  } = {}) {
  let url = `/${currentCategoryUrlKeyword}${CATEGORY_URL_DELIMITER}${currentCategoryId}`;

  if (brandIds) {
    url = `/${brandIds}${url}`;
  }

  if (queryParams) {
    const queryString = generateQueryString(queryParams);

    if (queryString) {
      url += queryString;
    }
  }

  return url;
}

function extractCategoryUrlKeywordAndId(categoryUrl) {
  let rawCategoryUrl;
  let categoryUrlKeyword, categoryId;

  if (categoryUrl[0] === '/') {
    rawCategoryUrl = categoryUrl;
  } else {
    rawCategoryUrl = '/' + categoryUrl;
  }

  const matchResult = matchUrlForRoutePath(rawCategoryUrl, ROUTE_PATHS[PLATFORMS.MOBILE].CATEGORY_PAGE);

  if (matchResult) {
    ({categoryUrlKeyword, categoryId} = matchResult.params);
  } else {
    categoryUrlKeyword = null;
    categoryId = null;
  }

  return {
    categoryUrlKeyword,
    categoryId,
  };
}

function extractBrandCategoryUrlKeywordAndId(categoryUrl) {
  let rawCategoryUrl;
  let categoryUrlKeyword, categoryId;

  if (categoryUrl[0] === '/') {
    rawCategoryUrl = categoryUrl;
  } else {
    rawCategoryUrl = '/' + categoryUrl;
  }

  const matchResult = matchUrlForRoutePath(rawCategoryUrl, ROUTE_PATHS[PLATFORMS.MOBILE].BRAND_CATEGORY_PAGE);

  if (matchResult) {
    ({categoryUrlKeyword, categoryId} = matchResult.params);
  } else {
    categoryUrlKeyword = null;
    categoryId = null;
  }

  return {
    categoryUrlKeyword,
    categoryId,
  };
}

function generateCategoryPageFilterQueryParams(selectedFiltersByFacetMap) {
  return generateFilterApiQueryParams(
    selectedFiltersByFacetMap,
    {
      ignoredFacets: [
        FACET_IDS.CATEGORY,
        FACET_IDS.BRAND,
      ],
    }
  );
}

function generateCategoryProductsApiQueryParams(selectedFiltersByFacetMap) {
  return generateFilterApiQueryParams(
    selectedFiltersByFacetMap,
    {
      ignoredFacets: [
        FACET_IDS.CATEGORY,
      ],
    }
  );
}

function generateCategoryBreadCrumbConfigs(categoryBreadCrumbArray) {
  return [{
    id: '/',
    labelText: 'Anasayfa',
    url: '/',
    isRoot: true,
    ...generateStructuredBreadCrumbDataProps(),
  }].concat(categoryBreadCrumbArray.map(
    breadCrumb => {
      return {
        ...breadCrumb,
        id: breadCrumb.url,
        labelText: breadCrumb.name,
        url: breadCrumb.url.startsWith('/') ? breadCrumb.url : '/' + breadCrumb.url,
        ...generateStructuredBreadCrumbDataProps(),
      };
    }
  ));
}

function isCategoryApiPaginationLimitExceeded(pageNumber, isToggledPageLimitActive = false) {
  return (
    (isToggledPageLimitActive &&
      parseInt(pageNumber) > TOGGLED_CATEGORY_PAGINATION_PAGE_NUMBER_LIMIT) ||
    isApiPaginationLimitExceeded(pageNumber)
  );
}

function generateCategoryHierarchyForDavinci(category) {
  const result = [];

  if (category?.parents) {
    result.push(...category.parents.map(parent => ({id: parent.categoryId, name: parent.categoryName})));
  }

  if (category) {
    result.push({id: category.categoryId, name: category.categoryName});
  }

  return result;
}


function generateCategoryHierarchyDsa(category, appliedFacetArray) {
  let result = '';

  if (category && category.parents) {
    category.parents.forEach(function(parentCategory) {
      result += '/' + parentCategory.categoryId;
    });
  }

  result += '/' + category.categoryId;

  if (appliedFacetArray) {
    appliedFacetArray.forEach(facet => {
      if (facet.rawFacet.id === FACET_IDS.BRAND && facet.rawFacet.items.length === 1) {
        result += '/' + facet.rawFacet.items[0].name;
      }
    });
  }
  return result;
}

function generateCategoryHierarchyForCategories(category, queryParams) {
  let result = [];

  result = category && category.children ?
    category.children.map(
      children => ({
        id: children.idHierarchy,
        name: children.categoryName,
        itemsCount: children.itemsCount,
        level: children.level,
        selected: children.idHierarchy === queryParams[FACET_QUERY_PARAM_NAMES.CATEGORY],
      })
    ) : [];

  if (category && category.categoryId) {
    result =
      category.children.map(
        children => ({
          id: children.idHierarchy,
          name: children.categoryName,
          itemsCount: children.itemsCount,
          level: children.level,
          selected: children.idHierarchy === queryParams[FACET_QUERY_PARAM_NAMES.CATEGORY],
        })
      )

    result.unshift({
      id: category.idHierarchy,
      name: category.categoryName,
      itemsCount: category.itemsCount,
      level: category.level,
      selected: category.idHierarchy === queryParams[FACET_QUERY_PARAM_NAMES.CATEGORY],
    });
  }

  if (category && category.parents.length > 0) {
    category.parents.forEach(
      parents => {
        const mapper = {
          id: parents.idHierarchy,
          name: parents.categoryName,
          itemsCount: parents.itemsCount,
          level: parents.level,
          selected: parents.idHierarchy === queryParams[FACET_QUERY_PARAM_NAMES.CATEGORY],
        };

        result.unshift(mapper);
      }
    );
  }

  return result;
}

function isBrandCategoryPage(url) {
  return Boolean(url.match(CATEGORY_PAGE_LINK_WITH_BRAND_REGEX));
}

function checkStartingSlash(url) {
  if(!url) {
    return '';
  }
  return url[0] === '/' ? url : '/' + url;
}

function generateSimilarChildCategoryFilters(category) {
  return category?.map?.(childCategory => ({
    id: childCategory?.categoryId,
    displayName: childCategory?.categoryName,
    href: checkStartingSlash(childCategory?.categoryUrl),
  })) ?? [];
}

function generateSimilarBrandFilter(brands){
  return brands?.map?.(brand => ({
    id: brand?.id,
    displayName: brand?.hrefTitle,
    href: checkStartingSlash(brand?.href),
  })) ?? [];
}

export {
  checkForCategoryRedirection,
  extractCategoryUrlKeywordAndId,
  extractBrandCategoryUrlKeywordAndId,
  generateCategoryUrl,
  generateCategoryPageFilterQueryParams,
  generateCategoryProductsApiQueryParams,
  generateCategoryBreadCrumbConfigs,
  isCategoryApiPaginationLimitExceeded,
  isBrandCategoryPage,
  generateCategoryHierarchyForDavinci,
  generateCategoryHierarchyDsa,
  generateCategoryHierarchyForCategories,
  generateSimilarChildCategoryFilters,
  generateSimilarBrandFilter,
};
